import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { isDefined, isSSR } from "./../../wix-rich-content-common";
import IframePlayer from "../IframePlayer";
import { ReactPlayerWrapper } from "../reactPlayerWrapper";
import styles from "../statics/styles/video-viewer-scss";
const VideoViewer = ({
  config,
  componentData,
  nodeId,
  isLight,
  onReady,
  isLoaded,
  seoMode,
  muted,
  onDuration,
  disabled
}) => {
  const [key, setKey] = useState("");
  const [state, setState] = useState(() => {
    const getInitialUrlState = () => {
      const { src } = componentData.video || {};
      if (src?.url) {
        const normalizedUrl = normalizeUrl(src.url);
        return {
          type: "READY",
          loadedUrl: normalizedUrl
        };
      }
      if (src?.id && config.getVideoUrl) {
        try {
          const videoUrl = config.getVideoUrl({ pathname: src.id });
          if (typeof videoUrl === "string") {
            return {
              type: "READY",
              loadedUrl: normalizeUrl(videoUrl),
              startingId: src.id
            };
          }
          return { type: "INITIAL" };
        } catch (e) {
          return { type: "INITIAL" };
        }
      }
      return { type: "INITIAL" };
    };
    const getInitialThumbnailUrlState = () => {
      const thumbnailId = componentData?.thumbnail?.src?.id;
      const thumbnailSrc = componentData?.thumbnail?.src?.url;
      if (thumbnailSrc) {
        return {
          type: "READY",
          loadedUrl: thumbnailSrc
        };
      }
      if (!thumbnailId || !config.getThumbnailUrl) {
        return { type: "INITIAL" };
      }
      try {
        const thumbnailUrl = config.getThumbnailUrl({ pathname: thumbnailId });
        if (typeof thumbnailUrl === "string") {
          return {
            type: "READY",
            loadedUrl: thumbnailUrl,
            startingId: thumbnailId
          };
        }
      } catch (e) {
        return { type: "INITIAL" };
      }
      return { type: "INITIAL" };
    };
    return {
      isLoaded: false,
      isVideoWrapperAttachedToDom: false,
      url: getInitialUrlState(),
      thumbnailUrl: getInitialThumbnailUrlState()
    };
  });
  useEffect(() => {
    setKey("mounted");
  }, []);
  useEffect(() => {
    const updateUrl = async () => {
      const { src } = componentData.video || {};
      const currentUrl = state.url;
      if (currentUrl.type === "LOADING") {
        return;
      }
      if (src?.url) {
        const normalizedUrl = normalizeUrl(src.url);
        if (currentUrl.type === "READY" && currentUrl.loadedUrl === normalizedUrl) {
          return;
        }
        setState((prev) => ({
          ...prev,
          url: {
            type: "READY",
            loadedUrl: normalizedUrl
          }
        }));
        return;
      }
      if (src?.id && config.getVideoUrl) {
        if (currentUrl.type === "READY" && currentUrl.startingId === src.id) {
          return;
        }
        setState((prev) => ({ ...prev, url: { type: "LOADING" } }));
        try {
          const videoUrl = await config.getVideoUrl({ pathname: src.id });
          setState((prev) => ({
            ...prev,
            url: {
              type: "READY",
              loadedUrl: normalizeUrl(videoUrl),
              startingId: src.id
            }
          }));
          return;
        } catch (e) {
          setState((prev) => ({ ...prev, url: { type: "READY", startingId: src.id } }));
          return;
        }
      }
      if (currentUrl.type !== "READY") {
        setState((prev) => ({ ...prev, url: { type: "READY" } }));
      }
    };
    updateUrl();
  }, [componentData.video, config.getVideoUrl, state.url]);
  useEffect(() => {
    const updateThumbnail = async () => {
      const { src } = componentData.thumbnail || {};
      const thumbnailId = src?.id;
      const thumbnailSrc = src?.url;
      const thumbnailState = state.thumbnailUrl;
      if (thumbnailSrc) {
        if (state.thumbnailUrl.type === "READY" && thumbnailSrc === state.thumbnailUrl.loadedUrl) {
          return;
        }
        setState((prev) => ({
          ...prev,
          thumbnailUrl: {
            type: "READY",
            loadedUrl: thumbnailSrc
          }
        }));
        return;
      }
      if (thumbnailState.type === "LOADING") {
        return;
      }
      if (!thumbnailId) {
        if (thumbnailState.type === "INITIAL") {
          setState((prev) => ({ ...prev, thumbnailUrl: { type: "READY" } }));
        }
        return;
      }
      if (!config.getThumbnailUrl) {
        if (thumbnailState.type === "INITIAL" || thumbnailState.type === "READY" && thumbnailState.startingId !== thumbnailId) {
          setState((prev) => ({ ...prev, thumbnailUrl: { type: "READY", startingId: thumbnailId } }));
        }
        return;
      }
      if (thumbnailState.type === "READY" && thumbnailState.startingId === thumbnailId && !!thumbnailState.loadedUrl) {
        return;
      }
      try {
        setState((prev) => ({ ...prev, thumbnailUrl: { type: "LOADING" } }));
        const thumbnailUrl = await config.getThumbnailUrl({ pathname: thumbnailId });
        setState((prev) => ({
          ...prev,
          thumbnailUrl: {
            type: "READY",
            loadedUrl: thumbnailUrl,
            startingId: thumbnailId
          }
        }));
      } catch (e) {
        setState((prev) => ({ ...prev, thumbnailUrl: { type: "READY", startingId: thumbnailId } }));
      }
    };
    updateThumbnail();
  }, [componentData.thumbnail, config.getThumbnailUrl, state.thumbnailUrl]);
  useEffect(() => {
    if (!state.isVideoWrapperAttachedToDom) {
      const videoRatio = getVideoRatio();
      if (videoRatio) {
        setVideoRatio(videoRatio.ratio, videoRatio.wrapper);
        setState((prev) => ({ ...prev, isVideoWrapperAttachedToDom: true }));
      }
    }
  }, [state.isVideoWrapperAttachedToDom]);
  const getVideoRatio = () => {
    const wrapper = ReactDOM.findDOMNode(void 0)?.parentNode;
    if (!wrapper) {
      return void 0;
    }
    const element = wrapper.querySelector("iframe, video");
    if (!element) {
      return void 0;
    }
    const ratio = element.clientHeight / element.clientWidth;
    return !isNaN(ratio) ? { ratio, wrapper } : void 0;
  };
  const setVideoRatio = (ratio, wrapper) => {
    wrapper.style["padding-bottom"] = ratio * 100 + "%";
  };
  const onReactPlayerReady = (player) => {
    if (onReady) {
      onReady(player);
      return;
    }
    if (!isLoaded) {
      setState((prev) => ({ ...prev, isLoaded: true }));
    }
  };
  const handleContextMenu = (e) => componentData.disableDownload && e.preventDefault();
  const getDownloadProps = () => {
    if (isDefined(componentData?.disableDownload) && componentData.disableDownload || isDefined(config?.disableDownload) && config.disableDownload) {
      return {
        config: {
          file: {
            attributes: {
              controlsList: "nodownload"
            }
          }
        }
      };
    }
    return {
      config: {}
    };
  };
  const getLightProps = () => {
    if (!isLight) {
      return {
        isLight: false
      };
    }
    return {
      isLight: true,
      thumbnail: state.thumbnailUrl.type === "READY" ? state.thumbnailUrl.loadedUrl : void 0
    };
  };
  const url = state.url.type === "READY" ? state.url.loadedUrl : void 0;
  if (!url) {
    return null;
  }
  const isCustomVideo = !!componentData.video?.src?.id;
  const shouldRenderIframe = isSSR() && seoMode && url;
  const { autoPlay = false, playInLoop = false, showControls = true } = componentData.options || {};
  return shouldRenderIframe ? /* @__PURE__ */ React.createElement(IframePlayer, { isCustomVideo, url }) : /* @__PURE__ */ React.createElement(
    ReactPlayerWrapper,
    {
      className: styles.video_player,
      onContextMenu: handleContextMenu,
      controls: isLoaded !== false && showControls,
      url,
      onReady: onReactPlayerReady,
      disabled,
      width: "100%",
      height: "100%",
      key,
      onDuration,
      onProgress: (progress) => {
        config.onProgress?.({ ...progress, id: nodeId });
      },
      progressInterval: config.progressInterval || 1e3,
      muted: muted || autoPlay,
      isLoaded: isLoaded || state.isLoaded,
      autoPlay,
      loop: playInLoop,
      isCustomVideo,
      ...getLightProps(),
      ...getDownloadProps()
    }
  );
};
const normalizeUrl = (url) => url && (url.toLowerCase().indexOf("vimeo") === 0 ? "https://" + url : url);
export {
  VideoViewer
};
//# sourceMappingURL=video-viewer.js.map
